import { useDataSource, useSnackbarController } from "@firecms/core";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LoadingButton,
  TextField,
  Typography,
} from "@firecms/ui";
import { useState } from "react";

import {
  buildConnectLinkUrl,
  ConnectLinkInput,
  copyConnectLink,
  validateConnectLinkInput,
} from "./apple_connect_link.utils";
import {
  APPLE_GRANT_STATUS_PATH,
  AppleGrantStatusEntity,
  describeLinkedCompany,
  isConnectLinkBlocked,
} from "./apple_grant_status.types";

// Two-step dialog for generating a connect link for a client who has NOT linked yet — the only case
// that needs one, since a client who has linked already has a row with its own copy action.
//
// Step 1 collects the two DearDoc ids. Step 2 is the guard: it reads
// `apple_grant_status/{accountId}` and either names the company already linked to that id (and
// refuses) or states plainly that nothing is on record (which is expected, and is what a first-time
// client looks like). The echo-back is the only defence against a typo that lands on a DIFFERENT
// REAL account — the CMS cannot validate `accountId` at all, because it lives in the dashboard GCP
// project. See `cms/docs/apple-connect-link-design.md`.

type Step =
  | { kind: "form" }
  | { kind: "blocked"; legalName: string; conflicted: boolean }
  | {
      kind: "confirm";
      input: ConnectLinkInput;
      url: string;
      /**
       * Present when a lapsed grant document exists for this account — a RE-link, not a first link.
       * A PRESENT object with an unknown company name is a different case from no document at all,
       * so this is an object rather than a nullable name string.
       */
      previous?: { linkedTo: string };
    }
  | { kind: "error"; message: string };

export function ConnectLinkDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const dataSource = useDataSource();
  const snackbarController = useSnackbarController();

  const [accountId, setAccountId] = useState("");
  const [salesforceId, setSalesforceId] = useState("");
  const [errors, setErrors] = useState<{ accountId?: string; salesforceId?: string }>({});
  const [step, setStep] = useState<Step>({ kind: "form" });
  const [checking, setChecking] = useState(false);

  const close = () => {
    onOpenChange(false);
    // Reset only after closing, so the dialog does not visibly flicker back to step 1.
    setAccountId("");
    setSalesforceId("");
    setErrors({});
    setStep({ kind: "form" });
  };

  const check = async () => {
    const validation = validateConnectLinkInput(accountId, salesforceId);
    if (!validation.valid) {
      setErrors({
        accountId: validation.accountIdError,
        salesforceId: validation.salesforceIdError,
      });
      return;
    }
    setErrors({});
    setChecking(true);
    try {
      const existing = await dataSource.fetchEntity<Partial<AppleGrantStatusEntity>>({
        path: APPLE_GRANT_STATUS_PATH,
        entityId: validation.input.accountId,
      });
      if (existing && isConnectLinkBlocked(existing.values)) {
        setStep({
          kind: "blocked",
          legalName: describeLinkedCompany(existing.values, "an Apple company"),
          conflicted: Boolean(existing.values.needsReview),
        });
        return;
      }
      setStep({
        kind: "confirm",
        input: validation.input,
        url: buildConnectLinkUrl(validation.input),
        previous: existing
          ? { linkedTo: describeLinkedCompany(existing.values, "a company Apple did not name") }
          : undefined,
      });
    } catch (e) {
      // Fail CLOSED. If the grant status cannot be read we do not know whether this account is
      // already linked. Since 2026-10-06 the backend also refuses to start for an active grant (the
      // link ends on the result page with GRANT_ACTIVE), but staff should learn that here, not from
      // the client.
      setStep({
        kind: "error",
        message: e instanceof Error ? e.message : "Could not read the Apple link status.",
      });
    } finally {
      setChecking(false);
    }
  };

  const copy = async () => {
    if (step.kind !== "confirm") return;
    const copied = await copyConnectLink(step.url);
    snackbarController.open(
      copied
        ? { type: "success", message: "Connect link copied. Send it to the client." }
        : {
            type: "error",
            message: "Could not copy automatically — select the link in the dialog and copy it.",
          },
    );
    if (copied) close();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())} maxWidth="xl">
      <DialogTitle variant="h6">Generate connect link</DialogTitle>

      <DialogContent className="flex flex-col gap-4">
        {step.kind === "form" && (
          <>
            <Typography variant="body2" color="secondary">
              For a client who has not linked their Apple Business Connect account yet. Both IDs are
              DearDoc&apos;s — the client never needs to know an Apple ID.
            </Typography>
            <TextField
              label="DearDoc account ID"
              value={accountId}
              error={Boolean(errors.accountId)}
              autoFocus
              onChange={(e) => setAccountId(e.target.value)}
            />
            {errors.accountId && (
              <Typography variant="caption" color="error">
                {errors.accountId}
              </Typography>
            )}
            <TextField
              label="Salesforce ID"
              value={salesforceId}
              error={Boolean(errors.salesforceId)}
              onChange={(e) => setSalesforceId(e.target.value)}
            />
            {errors.salesforceId && (
              <Typography variant="caption" color="error">
                {errors.salesforceId}
              </Typography>
            )}
          </>
        )}

        {step.kind === "blocked" && (
          <Alert color="warning">
            <Typography variant="subtitle2" gutterBottom>
              This account is already linked to {step.legalName}.
            </Typography>
            <Typography variant="body2">
              {step.conflicted
                ? "It is also flagged for review because more than one Apple company holds an active link. Do not send another link — ask an engineer to resolve the conflict first."
                : "They don't need a new link. Ask an engineer if this client genuinely needs to re-link."}
            </Typography>
          </Alert>
        )}

        {step.kind === "confirm" && (
          <>
            <Alert color="info">
              {step.previous
                ? `Previously linked to ${step.previous.linkedTo}, and that link is no longer active. Sending this link re-links them — check that company is the right client.`
                : "No Apple link on record for this account — expected for a client linking for the first time."}
            </Alert>
            <Typography variant="body2" color="secondary">
              Check these are the right client&apos;s IDs before sending. Nothing here can verify
              them for you.
            </Typography>
            <div className="flex flex-col gap-1">
              <Typography variant="caption" color="secondary">
                DearDoc account ID
              </Typography>
              <Typography variant="subtitle2">{step.input.accountId}</Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="caption" color="secondary">
                Salesforce ID
              </Typography>
              <Typography variant="subtitle2">{step.input.salesforceId}</Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="caption" color="secondary">
                Link
              </Typography>
              <Typography variant="caption" className="break-all select-all">
                {step.url}
              </Typography>
            </div>
          </>
        )}

        {step.kind === "error" && (
          <Alert color="error">
            <Typography variant="subtitle2" gutterBottom>
              Could not generate the link.
            </Typography>
            <Typography variant="body2">No link was generated. {step.message}</Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {step.kind === "form" && (
          <>
            <Button variant="text" onClick={close}>
              Cancel
            </Button>
            <LoadingButton loading={checking} variant="filled" onClick={check}>
              Check &amp; continue
            </LoadingButton>
          </>
        )}
        {step.kind === "confirm" && (
          <>
            <Button variant="text" onClick={() => setStep({ kind: "form" })}>
              Back
            </Button>
            <Button variant="filled" onClick={copy}>
              Copy link
            </Button>
          </>
        )}
        {step.kind === "error" && (
          <>
            <Button variant="text" onClick={close}>
              Close
            </Button>
            <LoadingButton loading={checking} variant="filled" onClick={check}>
              Retry
            </LoadingButton>
          </>
        )}
        {step.kind === "blocked" && (
          <Button variant="filled" onClick={close}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
